// 사용자 행동(액션) 단위로 그 행동이 유발한 API 요청들을 묶어 보는 경량 트레이서.
//
// 왜: "특정 페이지에서 어떤 요청이 얼마나 여러 개, 얼마나 단기간에 일어나는지"를 보려면
// 서버 로그·DB·네트워크 탭을 따로 보는 것으로는 부족하다. 사람의 한 동작(페이지 진입·클릭)이
// 연쇄로 일으킨 요청들을 하나의 묶음(액션)으로 봐야 "이 화면이 한 번에 8개를 쏜다" 같은
// 버벅임의 모양이 드러난다. 이 모듈이 그 액션 경계를 발급하고, API 요청을 액션에 귀속시킨다.
//
// 동작 범위:
//   - 액션 id 발급(startAction)·조회(getCurrentActionId)·요청 헤더 주입은 prod 에서도 돈다
//     (가벼운 변수 한 개 + 헤더 한 줄). 서버는 이 x-action-id 를 로그에 박아 화면↔서버를 잇는다.
//   - 요청 상세 기록(recordRequest)·버킷 보관·오버레이는 import.meta.env.DEV 에서만. prod 번들엔
//     사실상 영향이 없다(운영 관측은 Datadog RUM 이 담당).

const DEV = import.meta.env.DEV;
const MAX_ACTIONS = 50; // 링버퍼 — 최근 50개 액션만 보관(메모리 누수 방지).

export interface PerfRequest {
  method: string;
  url: string;
  /** null = 아직 진행 중이거나 네트워크 단계에서 실패(throw). */
  status: number | null;
  /** performance.now() 기준 시작 시각(ms). 액션 내 상대 위치·동시성 계산에 쓴다. */
  startMs: number;
  /** 응답까지 걸린 시간(ms). 진행 중이면 null. */
  durMs: number | null;
  /** content-length 헤더 기준 응답 크기(byte). 없으면 null(압축·스트림 등). */
  bytes: number | null;
}

export interface PerfAction {
  id: string;
  label: string;
  startedAt: number; // performance.now()
  requests: PerfRequest[];
}

function perfNow(): number {
  return typeof performance !== "undefined" ? performance.now() : 0;
}

function newId(): string {
  return typeof crypto?.randomUUID === "function" ? crypto.randomUUID() : `a${perfNow()}`;
}

let currentActionId = "boot";
const actions: PerfAction[] = DEV
  ? [{ id: currentActionId, label: "앱 부팅", startedAt: perfNow(), requests: [] }]
  : [];

const listeners = new Set<() => void>();
function notify(): void {
  for (const fn of listeners) fn();
}

/** 오버레이가 변경을 구독한다(DEV). 반환값은 해지 함수. */
export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * 새 액션 경계를 연다 — 이후 나가는 요청들은 이 액션으로 묶인다.
 * 페이지 이동·클릭 같은 "사람의 한 동작"마다 호출된다(useActionBoundary).
 */
export function startAction(label: string): string {
  const id = newId();
  currentActionId = id;
  if (DEV) {
    actions.push({ id, label, startedAt: perfNow(), requests: [] });
    while (actions.length > MAX_ACTIONS) actions.shift();
    notify();
  }
  return id;
}

/**
 * 지금 유효한 액션 id. 요청 진입 순간 읽어 그 요청에 고정한다 —
 * 토큰 갱신(await) 사이에 액션이 바뀌어도 요청이 엉뚱한 액션에 섞이지 않게 하기 위함.
 */
export function getCurrentActionId(): string {
  return currentActionId;
}

/**
 * 요청 1건의 기록을 시작하고, 종료를 찍는 콜백을 돌려준다. DEV 외에는 no-op.
 * 사용: const done = recordRequest(id, "GET", url); ... done(res.status, bytes)
 */
export function recordRequest(
  actionId: string,
  method: string,
  url: string,
): (status: number | null, bytes: number | null) => void {
  if (!DEV) return () => {};
  const startMs = perfNow();
  const entry: PerfRequest = { method, url, status: null, startMs, durMs: null, bytes: null };
  // 해당 액션을 찾는다. 링버퍼에서 이미 밀려났으면 가장 최근 액션에 귀속(드묾).
  const action = actions.find((a) => a.id === actionId) ?? actions[actions.length - 1];
  action?.requests.push(entry);
  notify();
  return (status, bytes) => {
    entry.status = status;
    entry.durMs = perfNow() - startMs;
    entry.bytes = bytes;
    notify();
  };
}

/** 최근 액션 버킷(오래된 → 최신). DEV 에서만 채워진다. */
export function getActions(): PerfAction[] {
  return actions;
}

export function clearActions(): void {
  if (DEV) {
    actions.length = 0;
    notify();
  }
}

// dev 콘솔에서 window.__perfTrace 로 직접 들여다본다.
if (DEV && typeof window !== "undefined") {
  (window as unknown as { __perfTrace: unknown }).__perfTrace = {
    getActions,
    clearActions,
    startAction,
  };
}
